import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { advertisers } from "../api/advertisers";
import { campaigns } from "../api/campaigns";
import { ApiError } from "../api/client";
import type { CampaignCreateCommand } from "../api/types";
import { CampaignForm } from "./CampaignForm";

export function CampaignsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["campaigns"], queryFn: campaigns.list });
  const advertiserList = useQuery({ queryKey: ["advertisers"], queryFn: advertisers.list });
  const create = useMutation({
    mutationFn: (command: CampaignCreateCommand) => campaigns.create(command),
    onSuccess: async (campaign) => {
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${campaign.id}`);
    },
    onError: (failure) => setError(failure instanceof ApiError && failure.status === 404 ? "Рекламодатель больше не активен." : "Кампания не сохранена. Проверьте поля."),
  });
  const activeAdvertisers = advertiserList.data?.filter((advertiser) => advertiser.status === "active") ?? [];

  return (
    <>
      <div className="page-heading">
        <div><h2>Кампании</h2><p className="muted">Расписание, атрибуция и назначения на плейсменты.</p></div>
        <button className="primary" type="button" disabled={advertiserList.isLoading || activeAdvertisers.length === 0} onClick={() => setCreating((value) => !value)}>{creating ? "Закрыть форму" : "Создать кампанию"}</button>
      </div>
      {advertiserList.isError && <p className="error">Не удалось загрузить рекламодателей.</p>}
      {!advertiserList.isLoading && activeAdvertisers.length === 0 && <p className="muted">Сначала создайте активного рекламодателя.</p>}
      {creating && activeAdvertisers.length > 0 && <><CampaignForm advertisers={activeAdvertisers} busy={create.isPending} onSubmit={(command) => { setError(null); create.mutate(command); }} />{error && <p className="error" role="alert">{error}</p>}</>}
      <div className="panel" style={{ marginTop: 16 }}>
        {list.isLoading && <p className="muted">Загружаю…</p>}
        {list.isError && <p className="error">Не удалось загрузить кампании. <button type="button" onClick={() => void list.refetch()}>Повторить</button></p>}
        {list.data?.length === 0 && <p className="muted">Кампаний пока нет.</p>}
        {list.data && list.data.length > 0 && <table><thead><tr><th>Название</th><th>Статус</th><th>Начало</th><th>Окончание</th><th>Назначения</th><th>Версия</th></tr></thead><tbody>{list.data.map((campaign) => <tr key={campaign.id}><td><Link to={`/campaigns/${campaign.id}`}>{campaign.name}</Link></td><td>{campaign.status}</td><td>{new Date(campaign.startsAt).toLocaleString("ru-RU")}</td><td>{campaign.endsAt ? new Date(campaign.endsAt).toLocaleString("ru-RU") : "—"}</td><td>{campaign.assignments.length}</td><td>{campaign.entityVersion}</td></tr>)}</tbody></table>}
      </div>
    </>
  );
}
